import { useTranslation } from "react-i18next";

import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { AddEvent, ChangeEvent, DeleteEvent } from "utils/history/Event";
import { asTitle } from "utils/Utils";

import { MultiSelectField, SelectOption } from "core/field/FieldService";
import { FormFieldExpansionProps } from "core/field/form/FormFieldRow";
import SelectFormOptions from "./SelectFormOptions";

export type MultiSelectFormFieldExpansionProps = FormFieldExpansionProps & {
	field: MultiSelectField;
};

const MultiSelectFormFieldExpansion: React.FC<
	MultiSelectFormFieldExpansionProps
> = (props: MultiSelectFormFieldExpansionProps) => {
	const { t } = useTranslation();

	function handleValueChange(value: string) {
		const event = new ChangeEvent("/value", props.field.value, value);
		props.onChange(event);
	}

	function addOption(option: SelectOption): void {
		const path = `/options/${props.field.options.length}`;
		props.onChange(new AddEvent(path, option));
	}

	function changeOption(index: number, value: string): void {
		const newValue = value.trim();
		const oldValue = props.field.options[index].value;
		const path = `/options/${index}/value`;
		if (newValue !== "" && newValue !== oldValue) {
			props.onChange(new ChangeEvent(path, oldValue, newValue));
		}
	}

	function removeOption(index: number): void {
		const path = `/options/${index}`;
		const option = props.field.options[index];
		props.onChange(new DeleteEvent(path, option));
	}

	function addAttachment(index: number, id: number): void {
		const attachmentIndex = props.field.options[index].attachments.length;
		const path = `/options/${index}/attachments/${attachmentIndex}`;
		props.onChange(new AddEvent(path, id));
	}

	function removeAttachment(index: number, attachmentIndex: number): void {
		const path = `/options/${index}/attachments/${attachmentIndex}`;
		const attachment =
			props.field.options[index].attachments[attachmentIndex];
		props.onChange(new DeleteEvent(path, attachment));
	}

	return (
		<>
			<Grid container alignItems="center">
				<Grid item xs={6} sm={4} md={3} lg={2}>
					<Typography variant="subtitle2">
						{asTitle(t("default"))}
					</Typography>
				</Grid>
				<Grid item xs={6} sm={4} md={3} lg={2}>
					<TextField
						id={props.field.name}
						select
						fullWidth
						disabled={!props.editable}
						value={props.field.value ?? ""}
						onChange={event =>
							handleValueChange(event.target.value)
						}
						SelectProps={{ multiple: true }}
						variant="standard">
						<MenuItem value="">
							<p style={{ margin: 12 }} />
						</MenuItem>
						{props.field.options?.map((option, index) => (
							<MenuItem key={index} value={option.value}>
								{option.value}
							</MenuItem>
						))}
					</TextField>
				</Grid>
			</Grid>

			<Typography variant="subtitle2">{asTitle(t("options"))}</Typography>
			<SelectFormOptions
				editable={props.editable}
				options={props.field.options}
				onAddOption={addOption}
				onChangeOption={changeOption}
				onRemoveOption={removeOption}
				onAddAttachment={addAttachment}
				onRemoveAttachment={removeAttachment}
			/>
		</>
	);
};

export default MultiSelectFormFieldExpansion;
